import React, { useState } from 'react';
import { FiX, FiThumbsUp, FiLoader } from 'react-icons/fi';

export interface RemoveFromBlocklistData {
  reason: string;
}

interface RemoveFromBlocklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: RemoveFromBlocklistData) => void;
  isLoading?: boolean;
  error?: string | null;
  itemValue?: string;
}

export const RemoveFromBlocklistModal: React.FC<RemoveFromBlocklistModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  error = null,
  itemValue,
}) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (reason) {
      onConfirm({ reason });
    }
  };

  const resetForm = () => {
    setReason('');
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const isFormValid = reason.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        {/* Header with Icon */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <FiThumbsUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="p-1 text-neutral-500 hover:text-neutral-700 transition-colors disabled:opacity-50"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-neutral-800 mb-2">Remover da Blacklist</h2>
        <p className="text-sm text-neutral-600 mb-6">
          {itemValue
            ? `Forneça o motivo para remover "${itemValue}" da Blacklist.`
            : 'Forneça o motivo para remover o item da Blacklist.'}
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Form */}
        <div className="space-y-4 mb-6">
          {/* Reason Textarea */}
          <div>
            <label className="block text-sm text-neutral-700 mb-2">
              Motivo da remoção <span className="text-red-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder="Descreva o motivo para remover este item da blacklist"
              rows={6}
              disabled={isLoading}
              maxLength={500}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm resize-none disabled:bg-neutral-100"
            />
            <span className="text-xs text-neutral-500 mt-1 block text-right">
              {reason.length}/500 caracteres
            </span>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-6 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg font-medium hover:bg-neutral-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && <FiLoader className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Removendo...' : 'Confirmar Remoção'}
          </button>
        </div>
      </div>
    </div>
  );
};
